import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Quote, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import LazyVimeo from "@/components/LazyVimeo";
import { useLanguage } from "@/hooks/use-language";
import { quotesById, type CustomerQuote } from "@/data/customerQuotes";
import { cn } from "@/lib/utils";
import teamDemoPhoto from "@/assets/photos/tiego-team-demo.webp";
import mattiasPhoto from "@/assets/team/mattias_grey.webp";
import kevinPhoto from "@/assets/team/kevin_grey.webp";
import { DemoButton } from "./PageHero";
import { Card, Chip, Section, SectionHeading, type Accent, type SectionTone } from "./Section";

/** Textlänk med pil, t.ex. "Läs mer om TGA →". */
export function ArrowLink({ to, children, dark }: { to: string; children: React.ReactNode; dark?: boolean }) {
  return (
    <Link
      to={to}
      className={cn(
        "group inline-flex items-center gap-2 font-medium transition-colors",
        dark ? "text-white hover:text-gold-light" : "text-navy hover:text-gold",
      )}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

/** Numrerade kort ("Tre saker gör TGA annorlunda", "Varför Tiego", inläsningssteg). */
export function NumberedCards({ items, accent = "gold" }: { items: { title?: string; body: string }[]; accent?: Accent }) {
  const cols =
    items.length >= 4 ? "md:grid-cols-2 lg:grid-cols-4" : items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ol className={cn("grid gap-6", cols)}>
      {items.map((item, i) => (
        <li key={i} className="rounded-2xl border border-border/50 bg-card p-7 shadow-card lg:p-8">
          <span
            className={cn(
              "mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full font-semibold",
              accent === "teal" ? "bg-teal/10 text-teal" : "bg-gold/10 text-gold",
            )}
          >
            {i + 1}
          </span>
          {item.title && <h3 className="mb-3 font-display text-xl font-medium text-foreground">{item.title}</h3>}
          <p className="leading-relaxed text-muted-foreground">{item.body}</p>
        </li>
      ))}
    </ol>
  );
}

/** Kundröster: filmen med Wall to Wall Group + citatkort. */
export function CustomerVoices({
  title,
  quoteIds,
  tone,
}: {
  title: string;
  quoteIds: CustomerQuote["id"][];
  tone?: SectionTone;
}) {
  const { t, language, localePath } = useLanguage();
  const quotes = quotesById(quoteIds);

  return (
    <Section tone={tone}>
      <SectionHeading eyebrow={t("Kundröster", "Customer voices")} title={title} />

      <div className="mt-12 grid overflow-hidden rounded-2xl border border-border/50 bg-card shadow-card lg:grid-cols-[3fr_2fr]">
        <div className="aspect-video bg-navy-deep">
          <LazyVimeo
            src="https://player.vimeo.com/video/748759690?dnt=1&title=0&byline=0&portrait=0"
            title={t("Kundberättelse – Philip Maazon, Wall to Wall Group", "Customer story – Philip Maazon, Wall to Wall Group")}
          />
        </div>
        <div className="flex flex-col justify-center p-7 lg:p-10">
          <h3 className="mb-3 font-display text-xl font-medium text-foreground">
            {t("Film: Philip Maazon, finanschef, Wall to Wall Group", "Film: Philip Maazon, CFO, Wall to Wall Group")}
          </h3>
          <p className="leading-relaxed text-muted-foreground">
            {t(
              "Philip berättar om implementationen och övergången till en effektivare koncernredovisning.",
              "Philip talks about the implementation and the move to more efficient group accounting.",
            )}
          </p>
        </div>
      </div>

      <div className={cn("mt-6 grid gap-6", quotes.length >= 3 ? "lg:grid-cols-3" : "md:grid-cols-2")}>
        {quotes.map((q) => (
          <figure key={q.id} className="flex flex-col rounded-2xl border border-border/50 bg-card p-7 shadow-card">
            <Quote className="mb-4 h-7 w-7 text-gold/60" aria-hidden />
            <blockquote className="flex-1 leading-relaxed text-foreground">”{q.text[language]}”</blockquote>
            <figcaption className="mt-6 flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                {q.initials}
              </span>
              <span className="text-sm">
                <strong className="block font-semibold text-foreground">{q.name}</strong>
                <span className="text-muted-foreground">{q.role[language]}</span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-10">
        <ArrowLink to={localePath("/tga/kundberattelser")}>{t("Läs hela kundberättelserna", "Read the full customer stories")}</ArrowLink>
      </div>
    </Section>
  );
}

/** ISAE 3402 – full version (startsidan) eller kompakt (TGA/TLE). */
export function IsaeSection({ variant, tone }: { variant: "full" | "compact"; tone?: SectionTone }) {
  const { t } = useLanguage();

  if (variant === "compact") {
    return (
      <Section tone={tone}>
        <div className="grid gap-10 lg:grid-cols-[2fr_3fr] lg:gap-16">
          <div>
            <SectionHeading eyebrow={t("Säkerhet", "Security")} title={t("Granskat enligt ISAE 3402", "Audited under ISAE 3402")} />
            <div className="mt-6 flex flex-wrap gap-2">
              <Chip>Type 1 · 2025</Chip>
              <Chip>{t("Type 2 · pågår 2026", "Type 2 · ongoing 2026")}</Chip>
              <Chip>ISO 27001</Chip>
            </div>
          </div>
          <p className="self-center text-lg leading-relaxed text-muted-foreground">
            {t(
              "Tiego har alltid arbetat strukturerat med IT-säkerhet och rutiner. Sedan 2025 granskas arbetet av en oberoende revisor enligt ISAE 3402, den internationella standarden för granskning av interna kontroller hos tjänsteleverantörer. Type 1 genomfördes 2025, Type 2-granskningen pågår 2026 och därefter följer löpande Type 2-granskningar. Arbetet har lett till konkreta förbättringar och är i dag en förutsättning när både mycket stora och mindre koncerner väljer system. Tjänsten penetrationstestades senast 2025, och vi arbetar systematiskt med informationssäkerhet enligt ISO 27001.",
              "Tiego has always worked in a structured way with IT security and routines. Since 2025 this work has been audited by an independent auditor under ISAE 3402, the international standard for assurance on internal controls at service organisations. Type 1 was completed in 2025, the Type 2 audit is under way in 2026, and recurring Type 2 audits follow. The work has led to concrete improvements and is now a requirement when both very large and smaller groups choose a system. The service was most recently penetration tested in 2025, and we work systematically with information security in line with ISO 27001.",
            )}
          </p>
        </div>
      </Section>
    );
  }

  const steps = [
    {
      icon: CheckCircle2,
      title: t("Type 1, genomförd 2025", "Type 1, completed 2025"),
      body: t(
        "Revisorn har bedömt att våra kontroller är ändamålsenligt utformade.",
        "The auditor has assessed that our controls are suitably designed.",
      ),
    },
    {
      icon: Clock,
      title: t("Type 2, pågår 2026", "Type 2, ongoing 2026"),
      body: t(
        "Revisorn prövar att kontrollerna faktiskt har fungerat under en hel period.",
        "The auditor tests that the controls have actually operated over a full period.",
      ),
    },
    { icon: RefreshCw, title: t("Framåt: löpande Type 2-granskningar", "Going forward: recurring Type 2 audits") },
  ];

  return (
    <Section tone={tone}>
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <SectionHeading eyebrow={t("Säkerhet och kontroll", "Security and control")} title={t("Granskat enligt ISAE 3402", "Audited under ISAE 3402")} />
          <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
            <p>
              {t(
                "Tiego har alltid arbetat strukturerat med IT-säkerhet och rutiner. Sedan 2025 granskas arbetet av en oberoende revisor enligt ISAE 3402, den internationella standarden för granskning av interna kontroller hos tjänsteleverantörer som hanterar kunders finansiella information.",
                "Tiego has always worked in a structured way with IT security and routines. Since 2025 this work has been audited by an independent auditor under ISAE 3402, the international standard for assurance on internal controls at service organisations that handle customers' financial information.",
              )}
            </p>
            <p>
              {t(
                "Arbetet har lett till konkreta förbättringar i hur vi utvecklar, driftar och dokumenterar. Det är i dag en förutsättning när både mycket stora och mindre koncerner väljer system. Tjänsten penetrationstestades senast 2025, och vi arbetar systematiskt med informationssäkerhet enligt ISO 27001.",
                "The work has led to concrete improvements in how we develop, operate and document. It is now a requirement when both very large and smaller groups choose a system. The service was most recently penetration tested in 2025, and we work systematically with information security in line with ISO 27001.",
              )}
            </p>
          </div>
        </div>
        <ul className="space-y-4 self-center">
          {steps.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-4 rounded-2xl border border-border/50 bg-card p-6 shadow-card">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                <Icon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <strong className="block font-semibold text-foreground">{title}</strong>
                {body && <span className="mt-1 block text-muted-foreground">{body}</span>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

/** "Byta system?" – avslutande CTA med foto, på alla tre sidorna. */
export function SwitchSystemCta({
  title,
  note,
  paragraphs,
  secondary,
  location,
  tone,
}: {
  title: string;
  note?: string;
  paragraphs: string[];
  secondary?: { label: string; to: string };
  location: string;
  tone?: SectionTone;
}) {
  const { t } = useLanguage();
  return (
    <Section tone={tone}>
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <img
          src={teamDemoPhoto}
          alt={t("Tiego-medarbetare samlade kring en dator i ett samtal", "Tiego employees gathered around a laptop in conversation")}
          width={1600}
          height={957}
          loading="lazy"
          decoding="async"
          className="w-full rounded-2xl object-cover shadow-card"
        />
        <div>
          <SectionHeading eyebrow={t("Byta system?", "Switching systems?")} title={title} lead={note} />
          <div className="mt-6 space-y-4 leading-relaxed text-muted-foreground">
            {paragraphs.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <DemoButton location={location} size="lg" className="h-12" />
            {secondary && (
              <Button asChild variant="outline" size="lg" className="h-12">
                <Link to={secondary.to}>{secondary.label}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

const PEOPLE = {
  mattias: { name: "Mattias Elfgren", email: "mattias.elfgren@tiego.se", photo: mattiasPhoto, system: "TGA" },
  kevin: { name: "Kevin Hall", email: "kevin.hall@tiego.se", photo: kevinPhoto, system: "TLE" },
} as const;

/** Kontaktperson med porträtt och mejl (inga direktnummer, enligt PM:et). */
export function ContactPersonCard({ person }: { person: keyof typeof PEOPLE }) {
  const { t } = useLanguage();
  const p = PEOPLE[person];
  return (
    <Card className="flex items-center gap-5 p-6 lg:p-6">
      <img
        src={p.photo}
        alt={t(`Porträtt av ${p.name}`, `Portrait of ${p.name}`)}
        width={72}
        height={72}
        loading="lazy"
        decoding="async"
        className="h-[72px] w-[72px] shrink-0 rounded-full object-cover"
      />
      <div className="min-w-0">
        <strong className="block font-semibold text-foreground">{p.name}</strong>
        <span className="block text-sm text-muted-foreground">
          {t(`Kontaktperson för ${p.system}`, `Contact for ${p.system}`)}
        </span>
        <a href={`mailto:${p.email}`} className="break-all text-sm text-navy underline-offset-4 hover:text-gold hover:underline">
          {p.email}
        </a>
      </div>
    </Card>
  );
}
